import React from "react";
import { saveAs } from "file-saver";
import { QuestionModel } from "../../models/QuestionModel";
import { BaseControl, ControlProps } from "./BaseControl";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getRendererProps } from "../renderers/BaseRenderer";
import FileUploadRenderer from "../renderers/v2/FileUploadRenderer";
import { translate } from "../../models/utilities/translationUtilities";
import { ThunkDispatchProp } from "../../models/actions/ThunkDispatchProp";
import { setVariableMentions, validationClear } from "../../redux/actions";
import { getFile, clearFile, uploadFile } from "../../redux/thunks/file";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";

interface Props extends ControlProps, ThunkDispatchProp {}

interface State {
    fileName: string;
    mimeType: string;
    size: number;
    fileBlob: Blob;
    status: string;
    progress: number;
    validationResult?: ValidationResultModel;
    isValid: boolean;
    errorMessage: string;
}

export class FileUploadControl extends BaseControl<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            fileName: "",
            size: 0,
            mimeType: "",
            fileBlob: null,
            status: "",
            progress: 0,
            isValid: true,
            errorMessage: "",
        };
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        this.updateState(props);
        const [variable] = props.question.variables;
        const mention = this.getMention(props, variable.name);
        if (mention) {
            const stateObject = JSON.parse(mention.value);
            props
                .dispatch(getFile(variable.name, stateObject.fileName, stateObject.mimeType))
                .then((data) => {
                    this.setState((state) => ({ ...state, fileBlob: data }));
                });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        this.updateState(nextProps);
    }

    updateState(props: Props) {
        this.setState((s) => {
            const newState = { ...s };
            const [variable] = props.question.variables;
            const mention = this.getMention(props, variable.name);
            if (mention) {
                const stateObject = JSON.parse(mention.value);
                newState.fileName = stateObject.fileName;
                newState.size = stateObject.size;
                newState.mimeType = stateObject.mimeType;
            }

            if (props.files.length > 0) {
                newState.status = props.files[0].status;
                newState.progress = props.files[0].progress;
            }
            return newState;
        });
    }

    onFileOpen = () => {
        const { fileBlob, fileName } = this.state;
        if (fileBlob) saveAs(fileBlob, fileName);
    };

    onClearFile = () => {
        this.clearFileSelection();
    };

    onFileSelection = (selectedFile: File) => {
        const { name, size, type } = selectedFile;
        const { question, matrix, dispatch } = this.props;
        const [variable] = question.variables;
        this.setState((state) => ({ ...state, fileBlob: new Blob([selectedFile]) }));

        this.props.dispatch(
            setVariableMentions(variable.name, (mentions) => {
                mentions.clear();
                mentions.add(
                    JSON.stringify({
                        fileName: name,
                        size,
                        mimeType: type,
                    }),
                );
                return mentions;
            }),
        );

        dispatch(validationClear());

        if (!this.isFileSizeValid(selectedFile.size)) {
            this.setInvalidState("InvalidFileSize");
            return;
        }

        if (!this.isMimeTypeValid(selectedFile.name)) {
            this.setInvalidState("InvalidFileType");
            return;
        }

        this.setState((state) => ({ ...state, isValid: true }));
        this.props.dispatch(uploadFile(variable.name, matrix, selectedFile));
    };

    setInvalidState = (errorKey) => {
        const message = translate(this.props.questionnaire, errorKey, this.props.language);
        this.setState((state) => ({ ...state, isValid: false, errorMessage: message }));
    };

    clearFileSelection = () => {
        const [variable] = this.props.question.variables;
        this.props.dispatch(clearFile(variable.name, this.props.matrix));
        this.setState({ fileName: "", size: 0, mimeType: "", fileBlob: null });
        this.setMentions(this.props, variable.name, (m) => m.clear());
    };

    renderQuestion() {
        const {
            fileName,
            size,
            mimeType,
            status,
            fileBlob,
            progress,
            isValid,
            errorMessage,
        } = this.state;
        const { theme, language } = this.props;
        const [variable] = this.props.question.variables;
        const browseFilesLabel = translate(theme, "BrowseFiles", language) || "BrowseFiles";
        const dragFilesLabel = translate(theme, "DragDropFiles", language) || "DragDropFiles";

        return (
            <FileUploadRenderer
                {...getRendererProps(this.props)}
                variableName={variable.name}
                fileName={fileName}
                fileSize={size}
                fileType={fileName.split(".").pop()}
                fileMime={mimeType}
                fileStatus={status}
                fileBlob={fileBlob}
                fileProgress={progress}
                onClearFile={this.onClearFile}
                onFileSelection={this.onFileSelection}
                onFileOpen={this.onFileOpen}
                browseFilesLabel={browseFilesLabel}
                dragFilesLabel={dragFilesLabel}
                isValid={isValid}
                uploadErrorMessage={errorMessage}
            />
        );
    }

    validate(): ValidationResultModel {
        const { question, useClientValidation, questionnaire } = this.props;

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const [variable] = question.variables;
        const required = this.isAnswerRequired(questionnaire, question);
        const mention = this.getMention(this.props, variable.name);

        if (required && !mention)
            return new ValidationBuilder().fail(variable.name, "IncompleteAnswer").build();

        if (mention) {
            const mentionValueObject = JSON.parse(mention.value);

            if (!this.isMimeTypeValid(mentionValueObject.fileName))
                return new ValidationBuilder().fail(variable.name, "InvalidFileType").build();

            if (!this.isFileSizeValid(mentionValueObject.size)) {
                const maxSize = parseFloat(getSettingString(question, "MaxFileSize")) * 1024;
                return new ValidationBuilder()
                    .fail(variable.name, "InvalidFileSize", { max: maxSize.toString() })
                    .build();
            }
        }
        return new ValidationBuilder().build();
    }

    isFileSizeValid(size: number): boolean {
        const maxSize = parseFloat(getSettingString(this.props.question, "MaxFileSize")) * 1024;
        if (maxSize && maxSize > 0 && size > maxSize) {
            return false;
        }

        return true;
    }

    isMimeTypeValid(fileName: string): boolean {
        const acceptsMimeTypes = getSettingString(this.props.question, "AcceptedFileTypes");
        let acceptsMimeType: string[] = [];
        if (acceptsMimeTypes && acceptsMimeTypes.length > 0) {
            acceptsMimeType = acceptsMimeTypes.split(",");
        }
        const fileExtension = fileName.split(".").pop();

        if (acceptsMimeType.length === 0) {
            return true;
        }

        return fileName !== undefined && acceptsMimeType.some((m) => m.includes(fileExtension));
    }
}
